import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { PafContradictionCard } from '@/pages/anatel/PafContradictionCard';
import {
  anatelPafConsistencyParams,
  fetchPafConsistencyCitations,
  fetchPafConsistencyOverview,
  fetchPafContradictions,
} from '@/services/anatel';

const PAGE_SIZE = 10;

/**
 * Consistency review for the FISCAL track.
 *
 * The sanctioning track has had this screen since the product shipped; the
 * fiscal one had the substrate for it sitting in the database unread. Two things
 * make this screen different from its sibling rather than a copy of it:
 *
 * 1. The money is a DISPUTED CREDIT, not a fine, so nothing here may be summed
 *    with or compared against a sanction distribution.
 * 2. INSUFFICIENT_EVIDENCE is a large category here — the model could not judge
 *    the pair — and that is not a quiet form of "no contradiction". It is shown
 *    as its own count rather than folded into the cleared pile.
 */
export function AnatelPafConsistencyPage() {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const [onlyVerified, setOnlyVerified] = useState(false);
  const [citation, setCitation] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  const overview = useQuery({
    queryKey: ['anatel', 'paf-consistency'],
    queryFn: fetchPafConsistencyOverview,
  });

  const citations = useQuery({
    queryKey: ['anatel', 'paf-consistency-citations'],
    queryFn: fetchPafConsistencyCitations,
    staleTime: Infinity,
  });

  const queue = useQuery({
    queryKey: ['anatel', 'paf-contradictions', onlyVerified, citation, offset],
    queryFn: () =>
      fetchPafContradictions({
        onlyFullyVerified: onlyVerified,
        citation,
        limit: PAGE_SIZE,
        offset,
      }),
    placeholderData: keepPreviousData,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">
          {t('anatel:pafConsistency.title')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('anatel:pafConsistency.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="anatel:pafConsistency.explainer" />

      <FieldGuide i18nKey="anatel:pafConsistency.guide" level={2} />

      <div className="flex justify-end">
        {/* The export replays the screen's query -- filters included. */}
        <ExportButton
          path={`/api/v1/anatel/export/paf/consistency?${anatelPafConsistencyParams({ onlyFullyVerified: onlyVerified, citation })}`}
        />
      </div>

      {overview.isPending ? (
        <SkeletonLoader rows={4} />
      ) : overview.error ? (
        <ErrorMessage message={overview.error.message} onRetry={() => void overview.refetch()} />
      ) : (
        <>
          <AsOfStrip asOf={overview.data.as_of} />

          <section className="rounded-lg border bg-card p-4">
            <Answer answer={overview.data.queue} help={t('anatel:pafConsistency.help')}>
              {(value) => (
                <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Stat
                    label={t('anatel:consistency.flagged')}
                    value={number.format(value.contradictions)}
                    hint={t('anatel:consistency.ofAdjudicated', {
                      n: number.format(value.adjudicated),
                    })}
                  />
                  <Stat
                    label={t('anatel:consistency.fullyVerified')}
                    value={number.format(value.contradictions_fully_verified)}
                    hint={t('anatel:consistency.spansConfirmed')}
                  />
                  <Stat
                    label={t('anatel:consistency.adjudicated')}
                    value={percent.format(value.adjudicated_share)}
                    hint={t('anatel:consistency.ofPairs', {
                      n: number.format(value.different_outcome_pairs),
                    })}
                  />
                  {/* Its own tile, not merged with NO_CONTRADICTION. "Could not
                      judge" and "found nothing wrong" are different answers. */}
                  <Stat
                    label={t('anatel:pafConsistency.undecided')}
                    value={number.format(
                      value.verdicts.find((row) => row.code === 'INSUFFICIENT_EVIDENCE')?.count ?? 0,
                    )}
                    hint={t('anatel:pafConsistency.undecidedHint')}
                  />
                </dl>
              )}
            </Answer>
          </section>
        </>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={onlyVerified}
            onChange={(event) => {
              setOnlyVerified(event.target.checked);
              setOffset(0);
            }}
          />
          {t('anatel:consistency.onlyVerified')}
        </label>

        {/* Options are the grounds pairs in THIS queue actually share. */}
        <label className="flex items-center gap-2 text-sm text-foreground">
          <span className="text-xs font-medium text-muted-foreground">
            {t('anatel:consistency.filters.citation')}
          </span>
          <select
            value={citation ?? ''}
            onChange={(event) => {
              setCitation(event.target.value || null);
              setOffset(0);
            }}
            className="max-w-md rounded-md border bg-background px-2 py-1.5 text-xs text-foreground"
          >
            <option value="">{t('anatel:consistency.filters.citationAny')}</option>
            {(citations.data ?? []).map((ground) => (
              <option key={ground} value={ground}>
                {ground}
              </option>
            ))}
          </select>
        </label>
      </div>

      {queue.isPending ? (
        <SkeletonLoader rows={10} />
      ) : queue.error ? (
        <ErrorMessage message={queue.error.message} onRetry={() => void queue.refetch()} />
      ) : queue.data.items.length === 0 ? (
        <EmptyState hint={t('anatel:pafConsistency.emptyHint')} />
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground" aria-live="polite">
            {t('anatel:decisions.showing', {
              from: number.format(offset + 1),
              to: number.format(offset + queue.data.items.length),
              total: number.format(queue.data.total),
            })}
          </p>

          {queue.data.items.map((item) => (
            <PafContradictionCard key={item.pair_key} item={item} />
          ))}

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
              className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-50"
            >
              {t('anatel:decisions.previous')}
            </button>
            <button
              type="button"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= queue.data.total}
              className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-50"
            >
              {t('anatel:decisions.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-xl font-semibold tabular-nums text-foreground">{value}</dd>
      <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>
    </div>
  );
}
